import { useEffect } from "react";

import CheckIcon from "assets/icons/CheckIcon";
import classNames from "classnames";
import styles from "./style.module.scss";
import { State } from "redux/store";
import { connect, useDispatch } from "react-redux";
import {
  getModelSelectionData,
  getEnrichmentProgress,
  getEnrichmentStatus,
} from "redux/Enrichment/selectors";
import { FETCH_ENRICHMENT_PROGRESS_INTERVAL } from "helpers/constants/miscellaneous";
import {
  fetchEnrichmentStatus,
  setModelSelectionData,
} from "redux/Enrichment/actions";
import { useParams } from "react-router";
import { ModelSelectionData, EnrichmentStatus } from "types";
import DangerCircleIcon from "assets/icons/DangerCircleIcon";

const ModelProgressModal = ({
  onAbortClick,
  onRetryClick,
  onExploreClick,
  onNewEnrichmentClick,
  enrichmentProgress,
  enrichmentStatus,
  modelSelectionData,
}: ModelProgressModalProps) => {
  const isProgressComplete = enrichmentProgress === 100;
  const enrichmentFailed =
    enrichmentStatus && enrichmentStatus === EnrichmentStatus.FAILED;
  const dispatch = useDispatch();
  const { enrichDatasetId } = useParams();
  useEffect(() => {
    if (isProgressComplete) {
      return;
    }
    dispatch(fetchEnrichmentStatus(enrichDatasetId!) as any);
    const intervalId = setInterval(() => {
      dispatch(fetchEnrichmentStatus(enrichDatasetId!) as any);
    }, FETCH_ENRICHMENT_PROGRESS_INTERVAL);
    return () => clearInterval(intervalId);
  }, [isProgressComplete, enrichDatasetId, dispatch]);

  const resetLocalData = () => {
    let enrichData = { ...modelSelectionData };

    if (!enrichData) {
      return;
    }

    if (enrichDatasetId && enrichData[enrichDatasetId]) {
      delete enrichData[enrichDatasetId];

      dispatch(setModelSelectionData(enrichData));
    }
  };

  const renderActionButtons = () => {
    if (!isProgressComplete) {
      return (
        <div className={styles.actionButtons}>
          <div
            data-testid="enrichment-progress-abort-btn"
            className={styles.abortButton}
            onClick={onAbortClick}
          >
            Abort
          </div>
          <div className={styles.exploreButton} onClick={onExploreClick}>
            Return to Explore
          </div>
        </div>
      );
    } else {
      if (enrichmentFailed) {
        return (
          <div className={styles.actionButtons}>
            <div
              data-testid="enrichment-progress-abort-btn"
              className={styles.abortButton}
              onClick={onAbortClick}
            >
              Abort
            </div>
            <div className={styles.retryButton} onClick={onRetryClick}>
              Retry
            </div>
          </div>
        );
      } else {
        return (
          <div className={styles.actionButtons}>
            <div
              className={styles.abortButton}
              onClick={() => {
                onNewEnrichmentClick();
                resetLocalData();
              }}
            >
              Run New Enrichment On this dataset
            </div>
            <div
              className={styles.exploreButton}
              onClick={() => {
                onExploreClick();
                resetLocalData();
              }}
            >
              View Enriched Dataset
            </div>
          </div>
        );
      }
    }
  };

  const infoMsg = enrichmentFailed
    ? "Your dataset enrichment was unsuccessful."
    : "Your dataset is being enriched.";

  const progressCompletion = enrichmentFailed
    ? "Failed"
    : `${enrichmentProgress}% Completed`;
  const progressMsg = enrichmentFailed
    ? "Please retry or contact support if the issue continues"
    : "This process will continue in the background";
  return (
    <div className={styles.modalOverlay}>
      <div className={styles.statusModal}>
        <div className={styles.modelTitle}>Enrichment Status</div>

        <div className={styles.divider}></div>
        <div className={styles.info}>{infoMsg}</div>
        <div className={styles.progressBar}>
          <div
            className={classNames(styles.progressBarStatus, {
              [styles.progressBarStatusFailure]: enrichmentFailed,
            })}
            style={{ width: `${enrichmentProgress}%` }}
          ></div>
        </div>
        <div className={styles.progressCount}>{progressCompletion}</div>
        <div
          className={classNames(
            styles.progressMessage,
            enrichmentFailed ? styles.errorColor : styles.successColor,
          )}
        >
          {enrichmentFailed ? (
            <DangerCircleIcon variant="filled" />
          ) : (
            <CheckIcon />
          )}
          <div className={styles.progressMessageText}>{progressMsg}</div>
        </div>
        {renderActionButtons()}
      </div>
    </div>
  );
};

const mapStateToProps = (state: State) => {
  return {
    enrichmentProgress: getEnrichmentProgress(state),
    enrichmentStatus: getEnrichmentStatus(state),
    modelSelectionData: getModelSelectionData(state),
  };
};

export default connect(mapStateToProps)(ModelProgressModal);

interface ModelProgressModalProps {
  onAbortClick: () => void;
  onRetryClick: () => void;
  onExploreClick: () => void;
  onNewEnrichmentClick: () => void;
  enrichmentProgress: number;
  enrichmentStatus: EnrichmentStatus | null;
  modelSelectionData: ModelSelectionData | null;
}
